/**
 * First Login Guard Component (Story 7A.4)
 *
 * Redirects users with requiresPasswordChange: true to /change-password.
 * Blocks access to all other routes until password is changed.
 *
 * Used for pre-created beta accounts that need to set their own password.
 *
 * FR59: Admin Panel Access (beta account management)
 *
 * @module components/auth/FirstLoginGuard
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useHousehold, useAuthLoading } from '../../stores/auth-store';

interface FirstLoginGuardProps {
  /** Child components to render if password change not required */
  children: React.ReactNode;
}

/**
 * Route guard that enforces password change for pre-created accounts
 *
 * Checks household.requiresPasswordChange flag and redirects to
 * /change-password if true. Allows access to /change-password itself.
 *
 * @example
 * ```tsx
 * // Wrap all family app routes in FirstLoginGuard
 * <FirstLoginGuard>
 *   <Routes>
 *     <Route path="/" element={<HomePage />} />
 *     ...
 *   </Routes>
 * </FirstLoginGuard>
 * ```
 */
export function FirstLoginGuard({ children }: FirstLoginGuardProps): JSX.Element {
  const household = useHousehold();
  const isLoading = useAuthLoading();
  const location = useLocation();

  // Don't block change-password page itself
  if (location.pathname === '/change-password') {
    return <>{children}</>;
  }

  // Also don't block auth routes (login, signup) or legal pages
  const publicPaths = [
    '/auth/login',
    '/auth/signup',
    // Story 23.2: social onboarding consent page. Already passes through for a
    // null household (this guard only redirects on requiresPasswordChange);
    // listed here explicitly for clarity.
    '/auth/complete-signup',
    '/auth/forgot-password',
    // Story 23.14: the reset-link recipient is typically unauthenticated (null
    // household) — whitelist so the guard's startsWith check lets them through.
    '/auth/reset-password',
    '/terms',
    '/privacy',
    '/about',
    '/help',
    '/admin', // Admin routes have their own auth
  ];

  if (publicPaths.some((path) => location.pathname.startsWith(path))) {
    return <>{children}</>;
  }

  // Don't redirect while still loading auth state
  if (isLoading) {
    return <>{children}</>;
  }

  // If household exists and requires password change, redirect
  if (household?.requiresPasswordChange) {
    return <Navigate to="/change-password" replace />;
  }

  // Normal access
  return <>{children}</>;
}
